/**
 * Ciri — „The Witcher: Steel and Silver".
 *
 * SOURCE: karty s ikonou 🔵 v banneri sú Source karty. Karta s dvoma efektmi
 * označenými číslom (napr. „1🔵 … / 5🔵 …") vyrieši **len jeden** — ten
 * s najvyšším číslom, ktoré je rovné alebo menšie ako počet Source kariet
 * v tvojom odhode. Pod nižším číslom sa nevyrieši nič. Karta, ktorá sa práve
 * hrá, ešte v odhode nie je. (Steel and Silver, „Ciri: Source")
 *
 * UNCONTAINABLE POWER: so 7+ Source kartami v odhode sa efekty tvojich kariet
 * nedajú zrušiť.
 */

import { shareZone } from '../map.js';
import type { Ctx } from '../ops.js';
import { askChoice, dealDamage, discardFromHand, discardRandom, drawCard, getMap, healFighter, heroOf } from '../ops.js';
import { areEnemies } from '../players.js';
import type { PlayerIndex, SpaceId } from '../types.js';
import { neighbors } from '../map.js';
import { feint } from './common.js';
import { askMove, fighterOrNull } from './helpers.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';

export const SOURCE = 'source';
const CIRI = 'ciri';
const IHUARRAQUAX = 'ihuarraquax';

/** Koľko Source kariet leží v odhode hráča. */
export function sourceCount(ctx: Ctx, player: PlayerIndex): number {
  const discard = ctx.state.players[player]?.discard ?? [];
  return discard.filter((id) => CIRI_CARDS[ctx.state.cards[id]?.defId ?? '']?.keywords?.includes(SOURCE)).length;
}

/**
 * Ktorý z dvoch efektov karty platí: 2 = vyšší, 1 = nižší, 0 = žiadny.
 * „Resolve the effect that has the highest number that is equal to or less
 * than the number of Source cards in your discard pile."
 */
function tier(ctx: Ctx, player: PlayerIndex, low: number, high: number): 0 | 1 | 2 {
  const count = sourceCount(ctx, player);
  if (count >= high) return 2;
  if (count >= low) return 1;
  return 0;
}

const ciriOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === CIRI) ?? null;

/** Vzdialenosť v krokoch po čiarach mapy (bez ohľadu na figúrky). */
function within(ctx: Ctx, from: SpaceId, max: number): Set<SpaceId> {
  const map = getMap(ctx);
  const seen = new Set<SpaceId>([from]);
  let frontier = [from];
  for (let step = 0; step < max; step++) {
    const next: SpaceId[] = [];
    for (const id of frontier) {
      for (const n of neighbors(map, id)) {
        if (seen.has(n)) continue;
        seen.add(n);
        next.push(n);
      }
    }
    frontier = next;
  }
  return seen;
}

/** „1🔵 This card's value is 4 instead. 5🔵 This card's value is 7 instead." */
const lionCubOfCintra: CardEffect = {
  keywords: [SOURCE],
  during: (e, value) => {
    const t = tier(e.ctx, e.player, 1, 5);
    return t === 2 ? 7 : t === 1 ? 4 : value;
  },
};

/**
 * „You may discard any number of 🔵 cards from your hand. Add +2 to this
 * card's value for each 🔵 card you discarded." Po jednej karte — kým hráč
 * nepovie dosť alebo mu Source karty v ruke nedôjdu.
 */
function askChannel(ctx: Ctx, player: PlayerIndex, cardId: string, side: 'attacker' | 'defender'): void {
  askChoice(ctx, {
    kind: 'card',
    player,
    source: cardId,
    label: 'Channel the Source: odhoď Source kartu 🔵 za +2 (koľko chceš)',
    optional: true,
    from: 'hand',
    purpose: 'effect',
    keyword: SOURCE,
    discards: true,
    side,
  });
}

const channelTheSource: CardEffect = {
  duringChoice: (e) => askChannel(e.ctx, e.player, e.cardId, e.role === 'defender' ? 'defender' : 'attacker'),
  onCardChosen: (ctx, choice, cardId) => {
    const combat = ctx.state.combat;
    if (!combat || !choice.source) return;
    discardFromHand(ctx, choice.player, cardId, 'effect');
    const side = choice.side ?? 'attacker';
    combat.boosts ??= { attacker: 0, defender: 0 };
    combat.boosts[side] += 2;
    askChannel(ctx, choice.player, choice.source, side);
  },
};

/**
 * „Move Ihuarraquax up to 4 spaces. He may move through opposing fighters.
 * Then, deal 1 damage to an opposing fighter he moved through."
 */
const baneOfTheAenElle: CardEffect = {
  after: (e) => {
    if (fighterOrNull(e.ctx, e.fighterId)?.defId !== IHUARRAQUAX) return;
    askMove(e, {
      fighters: [e.fighterId!],
      max: 4,
      through: true,
      label: 'Bane of the Aen Elle: presuň Ihuarraquaxa až o 4 (smie prejsť cez súperov)',
      follow: { kind: 'damageThrough', amount: 1, label: 'Bane of the Aen Elle: 1 poškodenie figúrke, cez ktorú prešiel' },
    });
  },
};

/**
 * „2🔵 Deal 1 damage to the opposing fighter.
 *  7🔵 Deal 2 damage to an opposing fighter within 3 spaces of Ciri."
 */
const pushedToTheBrink: CardEffect = {
  keywords: [SOURCE],
  after: (e) => {
    const t = tier(e.ctx, e.player, 2, 7);
    if (t === 1 && e.opponentFighterId) dealDamage(e.ctx, e.opponentFighterId, 1);
    if (t === 2) {
      const ciri = ciriOf(e.ctx, e.player);
      if (!ciri) return;
      const near = within(e.ctx, ciri.spaceId, 3);
      askChoice(e.ctx, {
        kind: 'damage',
        player: e.player,
        source: e.cardId,
        label: 'Pushed to the Brink: 2 poškodenia súperovej figúrke do 3 priestorov od Ciri',
        optional: false,
        amount: 2,
        targets: e.ctx.state.fighters
          .filter((f) => areEnemies(e.ctx.state, f.owner, e.player) && near.has(f.spaceId))
          .map((f) => f.id),
      });
    }
  },
};

/**
 * „3🔵 Return a card from your discard pile to the top of your deck.
 *  8🔵 Return a card from your discard pile to your hand. Draw 1 card."
 */
const theLadyOfSpaceAndTime: CardEffect = {
  keywords: [SOURCE],
  after: (e) => {
    const t = tier(e.ctx, e.player, 3, 8);
    if (t === 0 || (e.ctx.state.players[e.player]?.discard.length ?? 0) === 0) {
      if (t === 2) drawCard(e.ctx, e.player);
      return;
    }
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label:
        t === 2
          ? 'The Lady of Space and Time: vezmi si kartu z odhodu do ruky'
          : 'The Lady of Space and Time: polož kartu z odhodu navrch balíčka',
      optional: false,
      from: 'discard',
      purpose: t === 2 ? 'toHand' : 'topOfDeck',
    });
    if (t === 2) drawCard(e.ctx, e.player);
  },
};

/**
 * „3🔵 This card's value is 4 instead.
 *  5🔵 This card's value is 4 instead. Your opponent discards 1 random card."
 */
const zireael: CardEffect = {
  keywords: [SOURCE],
  duringChoice: (e) => {
    if (tier(e.ctx, e.player, 3, 5) === 2 && e.opponentPlayer !== null) {
      discardRandom(e.ctx, e.opponentPlayer, true);
    }
  },
  during: (e, value) => (tier(e.ctx, e.player, 3, 5) > 0 ? 4 : value),
};

/** „Move Ciri up to 3 spaces. She may move through opposing fighters." */
const blink: CardEffect = {
  after: (e) => {
    const ciri = ciriOf(e.ctx, e.player);
    if (!ciri) return;
    askMove(e, {
      fighters: [ciri.id],
      max: 3,
      through: true,
      label: 'Blink: presuň Ciri až o 3 (smie prejsť cez súperov)',
    });
  },
};

/** „If you won the combat, you may search your deck for a Source card, reveal it, and add it to your hand." */
const searchingStrike: CardEffect = {
  after: (e) => {
    if (!e.won) return;
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label: 'Searching Strike: smieš si z balíčka vziať Source kartu 🔵',
      optional: true,
      from: 'deck',
      purpose: 'toHand',
      keyword: SOURCE,
    });
  },
};

/**
 * „4🔵 Deal 1 damage to each other fighter in Ciri's zone.
 *  9🔵 Deal 3 damage to each other fighter in Ciri's zone." — aj vlastným.
 */
const childOfTheElderBlood: CardEffect = {
  keywords: [SOURCE],
  scheme: (e) => {
    const t = tier(e.ctx, e.player, 4, 9);
    const ciri = ciriOf(e.ctx, e.player);
    if (t === 0 || !ciri) return;
    const amount = t === 2 ? 3 : 1;
    const map = getMap(e.ctx);
    const ids = e.ctx.state.fighters
      .filter((f) => f.id !== ciri.id && shareZone(map, f.spaceId, ciri.spaceId))
      .map((f) => f.id);
    for (const id of ids) dealDamage(e.ctx, id, amount);
  },
};

/**
 * „Ciri recovers 2 health. ONGOING: After each combat your fighter is in,
 * draw 1 card. Discard this card at the end of your turn if you have 5 or
 * more cards in your hand."
 */
const unicornAlly: CardEffect = {
  scheme: (e: EffectCtx) => {
    const ciri = ciriOf(e.ctx, e.player) ?? heroOf(e.ctx.state, e.player);
    if (ciri) healFighter(e.ctx, ciri.id, 2);
  },
  ongoing: {
    discardAtEnd: (ctx, player) => (ctx.state.players[player]?.hand.length ?? 0) >= 5,
    afterCombat: (ctx, player) => {
      drawCard(ctx, player);
    },
  },
};

const CIRI_CARDS: EffectRegistry = {
  'ciri.lion-cub-of-cintra': lionCubOfCintra,
  'ciri.channel-the-source': channelTheSource,
  'ciri.bane-of-the-aen-elle': baneOfTheAenElle,
  'ciri.pushed-to-the-brink': pushedToTheBrink,
  'ciri.the-lady-of-space-and-time': theLadyOfSpaceAndTime,
  'ciri.zireael': zireael,
  'ciri.blink': blink,
  'ciri.searching-strike': searchingStrike,
  'ciri.parry': feint,
  'ciri.child-of-the-elder-blood': childOfTheElderBlood,
  'ciri.unicorn-ally': unicornAlly,
};

export const ciriCards: EffectRegistry = CIRI_CARDS;

/** UNCONTAINABLE POWER — „(7🔵) Effects on your cards cannot be canceled." */
export const ciriSpecial: HeroSpecial = {
  name: 'Uncontainable Power',
  cardsUncancelable: (ctx, player) => sourceCount(ctx, player) >= 7,
};
